import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';

class SummaryData extends EmberObject {
  @computed('invoiced', 'unpaid')
  get unpaidPercent() {
    if (isEmpty(this.invoiced)) {
      return 0;
    }
    return Number((this.unpaid / this.invoiced) * 100).toFixed(2);
  }
}

export default class LedgerAnalysisController extends Controller {
  @service
  strings;

  @tracked
  from = '';

  @tracked
  to = '';

  @tracked
  _reportTitle = null;

  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  @computed('from', 'to', 'strings.financeLedger', '_reportTitle')
  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    return `${this.strings.financeLedger} Analysis: ${this.from} - ${this.to}`;
  }

  @computed('model.[]')
  get data() {
    let data = EmberObject.create({});
    const reducer = (data, item) => {
      let ledger = item.ledger;
      let ledgerId = ledger.get('id');
      let ledgerItem = item.ledgerItem;
      if (isEmpty(data[ledgerId])) {
        // We haven't seen this ledger yet; add it to the map.
        data.set(
          ledgerId,
          SummaryData.create({
            ledger,
            items: [],
            invoiced: 0,
            paid: 0,
            discounted: 0,
            refunded: 0,
            voided: 0,
            writtenOff: 0,
            transferred: 0,
            unpaid: 0,
          }),
        );
      }

      if (isEmpty(ledgerItem.get('id'))) {
        // If there's no Ledger Item, create a dummy one (per Ledger) called "Other".
        ledgerItem = EmberObject.create({
          id: 0,
          name: 'Other',
        });
      }

      let itemId = ledgerItem.get('id');
      if (isEmpty(data.get(ledgerId).items[itemId])) {
        // We haven't seen this Ledger Item yet; add it to the map.
        data[ledgerId].items[itemId] = SummaryData.create({
          item: ledgerItem,
          invoiced: 0,
          paid: 0,
          discounted: 0,
          refunded: 0,
          voided: 0,
          writtenOff: 0,
          transferred: 0,
          unpaid: 0,
        });
      }

      // Update both ledger and ledger item amounts.

      let paid = 0,
        discounted = 0,
        refunded = 0,
        voided = 0,
        writtenOff = 0,
        transferred = 0;

      item.transactions.forEach((transaction) => {
        if (transaction.isCancelled) {
          // Don't add cancelled transactions to the totals.
          return;
        }
        switch (transaction.type) {
          case TransactionType.APPORTIONMENT:
            paid += transaction.displayAmount;
            break;
          case TransactionType.TRANSFER:
            transferred += transaction.displayAmount;
            break;
          case TransactionType.DISCOUNT___EXEMPTION:
            discounted += transaction.displayAmount;
            break;
          case TransactionType.REFUND:
            refunded += transaction.amount;
            break;
          case TransactionType.VOID:
            voided += transaction.displayAmount;
            break;
          case TransactionType.WRITEOFF:
            writtenOff += transaction.displayAmount;
        }
      });

      // Add values to Ledger Item
      data[ledgerId].items[itemId].incrementProperty(
        'invoiced',
        item.displayAmount,
      );
      data[ledgerId].items[itemId].incrementProperty('unpaid', item.balance);
      data[ledgerId].items[itemId].incrementProperty('paid', paid);
      data[ledgerId].items[itemId].incrementProperty('discounted', discounted);
      data[ledgerId].items[itemId].incrementProperty('refunded', refunded);
      data[ledgerId].items[itemId].incrementProperty('voided', voided);
      data[ledgerId].items[itemId].incrementProperty('writtenOff', writtenOff);
      data[ledgerId].items[itemId].incrementProperty(
        'transferred',
        transferred,
      );

      // Add values to Ledger
      data[ledgerId].incrementProperty('invoiced', item.displayAmount);
      data[ledgerId].incrementProperty('unpaid', item.balance);
      data[ledgerId].incrementProperty('paid', paid);
      data[ledgerId].incrementProperty('discounted', discounted);
      data[ledgerId].incrementProperty('refunded', refunded);
      data[ledgerId].incrementProperty('voided', voided);
      data[ledgerId].incrementProperty('writtenOff', writtenOff);
      data[ledgerId].incrementProperty('transferred', transferred);

      return data;
    };

    return this.model.reduce(reducer, data);
  }

  @computed('data')
  get totals() {
    let totals = {
      invoiced: 0,
      paid: 0,
      discounted: 0,
      refunded: 0,
      voided: 0,
      writtenOff: 0,
      transferred: 0,
      unpaid: 0,
    };
    Object.keys(this.data).forEach((ledgerId) => {
      totals.invoiced += this.data.get(ledgerId).invoiced;
      totals.paid += this.data.get(ledgerId).paid;
      totals.discounted += this.data.get(ledgerId).discounted;
      totals.refunded += this.data.get(ledgerId).refunded;
      totals.voided += this.data.get(ledgerId).voided;
      totals.writtenOff += this.data.get(ledgerId).writtenOff;
      totals.transferred += this.data.get(ledgerId).transferred;
      totals.unpaid += this.data.get(ledgerId).unpaid;
    });
    return EmberObject.create(totals);
  }
}
